import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { setAllApiSpecData } from '~/lib/stores/integrations';
import { Modal } from '~/components/ui/Modal';

interface ApiClientOption {
  value: string;
  label: string;
  isDefault?: boolean;
}

const frameworkApiClients: Record<string, ApiClientOption[]> = {
  FLUTTER: [
    {
      value: 'HTTP',
      label: 'HTTP',
      isDefault: true,
    },
  ],
  DEFAULT: [
    {
      value: 'Fetch',
      label: 'Fetch',
      isDefault: true,
    },
    {
      value: 'Axios',
      label: 'Axios',
    },
  ],
};

interface CodeIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  selectedFramework: string;
}

const CodeIntegration: React.FC<CodeIntegrationProps> = ({ isOpen, onClose, onSave, selectedFramework }) => {
  const { t } = useTranslation();
  const [selectedApi, setSelectedApi] = useState<string>('Fetch');
  const [openApiSpec, setOpenApiSpec] = useState('');
  const [selectedOption, setSelectedOption] = useState<'infer' | 'paste'>('paste');
  const [giveYamlSpec, setGiveYamlSpec] = useState(false);

  const handleSelectApi = (api: string) => {
    setSelectedApi(api);
    console.log('api', api);
  };

  const handleSpecChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOpenApiSpec(e.target.value);
  };

  const handleSave = () => {
    if (selectedOption === 'infer') {
      // Set all API spec data in the store
      setAllApiSpecData('DUALITEX_INFER_YOURSELF', giveYamlSpec, 'Fetch');
    } else {
      console.log('openApiSpec', openApiSpec);
      setAllApiSpecData(openApiSpec, false, selectedApi);
    }

    // Notify parent that save was completed
    onSave?.();
    onClose();
  };

  // Get API client options based on framework
  const getApiClientOptions = () => {
    console.log('selectedFramework >>>>', selectedFramework);

    if (selectedFramework.toLowerCase().includes('flutter')) {
      return frameworkApiClients.FLUTTER;
    }

    return frameworkApiClients.DEFAULT;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full px-4 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 max-w-[520px]">
        <div className="dark:bg-[#1A1A1A] bg-dualite-background-secondary border border-dualite-border-primary rounded-lg shadow-2xl relative w-full overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 text-dualite-text-secondary bg-transparent border-none rounded-md hover:bg-dualite-background-button-hover transition-colors z-10"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>

          <div className="p-4 leading-normal">
            <div className="flex flex-col space-y-3">
              <div className="flex flex-col pr-6">
                <h2 className="text-base font-semibold text-dualite-text-primary leading-snug">
                  {t('modals.codeIntegration.title')}
                </h2>
                <p className="text-xs text-dualite-text-secondary leading-relaxed">
                  {t('modals.codeIntegration.subtitle')}
                </p>
              </div>

              <div className="h-px bg-[#414142] my-2 -mx-6" />

              <div
                className={`p-4 rounded-lg border cursor-pointer
                  ${selectedOption === 'infer' ? 'border-dualite-background-cta bg-dualite-background-button-hover/5' : 'border-dualite-border-primary'}`}
                onClick={() => setSelectedOption('infer')}
              >
                <div className="flex justify-between items-start">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-dualite-text-secondary font-medium leading-snug">
                      {t('modals.codeIntegration.inferTitle')}
                    </p>
                    <p className="text-xs text-dualite-text-secondary/70 leading-relaxed">
                      {t('modals.codeIntegration.inferDescription')}
                    </p>
                  </div>
                </div>

                {selectedOption === 'infer' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-3 pt-3 border-t border-dualite-border-primary"
                  >
                    <label
                      className="flex items-center gap-3 cursor-pointer group"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={giveYamlSpec}
                          onChange={(e) => setGiveYamlSpec(e.target.checked)}
                          className="sr-only" // Hide the actual checkbox but keep it accessible
                        />
                        <div
                          className={`w-5 h-5 rounded border ${
                            giveYamlSpec
                              ? 'bg-dualite-primary-start border-dualite-primary-start'
                              : 'border-dualite-border-secondary/80 bg-dualite-background-tertiary group-hover:border-dualite-border-secondary'
                          } flex items-center justify-center transition-colors`}
                        >
                          {giveYamlSpec && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5 text-white"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-dualite-text-muted group-hover:text-dualite-text-secondary transition-colors leading-relaxed">
                        {t('modals.codeIntegration.generateYamlSpec')}
                      </span>
                    </label>
                  </motion.div>
                )}
              </div>

              <div
                className={`p-4 rounded-lg border cursor-pointer
                    ${selectedOption === 'paste' ? 'border-dualite-background-cta bg-dualite-background-button-hover/5' : 'border-dualite-border-primary'}`}
                onClick={() => setSelectedOption('paste')}
              >
                <p className="text-sm text-dualite-text-secondary font-medium leading-snug">
                  {t('modals.codeIntegration.pasteTitle')}
                </p>
                <p className="text-xs text-dualite-text-secondary/70 mb-0 leading-relaxed">
                  {t('modals.codeIntegration.pasteDescription')}
                </p>

                {selectedOption === 'paste' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-3 flex flex-col space-y-3"
                  >
                    <div className="relative">
                      <select
                        value={selectedApi}
                        onChange={(e) => handleSelectApi(e.target.value)}
                        className="w-full h-9 px-3 flex items-center justify-between bg-dualite-background-tertiary text-dualite-text-secondary text-left text-sm border border-dualite-border-primary rounded-lg appearance-none cursor-pointer transition-colors duration-200 focus:outline-none focus:border-blue-500"
                      >
                        {!selectedApi && <option value="">{t('modals.codeIntegration.selectApiCall')}</option>}
                        {getApiClientOptions().map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            className="bg-dualite-background-tertiary text-dualite-text-secondary"
                          >
                            {option.label} {option.isDefault && '(Default)'}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none text-dualite-text-secondary/60" />
                    </div>

                    <textarea
                      placeholder={t('modals.codeIntegration.pastePlaceholder')}
                      value={openApiSpec === 'INFER_YOURSELF' ? '' : openApiSpec}
                      onChange={handleSpecChange}
                      className="w-full h-[120px] p-3 bg-dualite-background-tertiary text-dualite-text-secondary border border-dualite-border-primary rounded-lg placeholder-dualite-text-placeholder text-sm leading-relaxed focus:outline-none focus:border-blue-500"
                    />
                  </motion.div>
                )}
              </div>

              <div className="h-px bg-[#414142] my-3 -mx-6" />

              <div>
                <div className="mb-4 p-4 bg-dualite-background-tertiary border border-dualite-border-secondary/10 rounded-lg">
                  <h3 className="text-sm font-medium text-dualite-text-tertiary mb-2">
                    {t('modals.codeIntegration.whatIsApiSpec')}
                  </h3>
                  <p className="text-xs text-dualite-text-tertiary/70">
                    {t('modals.codeIntegration.apiSpecDescription')}
                    <a
                      href="https://swagger.io/solutions/getting-started-with-oas/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dualite-primary-start hover:text-dualite-primary-end transition-colors underline underline-offset-2 ml-1"
                    >
                      {t('modals.codeIntegration.learnMore')}
                    </a>
                  </p>
                </div>
              </div>

              {/* <span className="text-dualite-text-heading-start text-sm">Codebase Connect</span> */}

              {/* <div className="relative">
                <label
                  className={`flex items-center px-3 h-9 border border-[#60606140] 
                  rounded-lg text-[#929292] cursor-pointer
                  ${!isFrameworkSupported && 'opacity-50 cursor-not-allowed'}
                  hover:bg-[#333333] hover:text-[#E7E7E7]`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  <span className="text-sm ">Import your codebase</span>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".js,.ts,.jsx,.tsx"
                    disabled={!isFrameworkSupported}
                  />
                </label>
                {!isFrameworkSupported && (
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2
                    px-2 py-1 text-xs text-white bg-[#383838] rounded-full"
                  >
                    Coming Soon
                  </span>
                )}
              </div>

              <div className="relative">
                <label
                  className={`flex items-center px-3 h-9 border border-[#60606140] 
                  rounded-lg text-[#929292]
                  ${!isFrameworkSupported && 'opacity-50 cursor-not-allowed'}
                  ${isFrameworkSupported && 'cursor-pointer hover:bg-[#333333] hover:text-[#E7E7E7]'}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  <span className="text-sm ">Import your theme</span>
                  <input type="file" className="hidden" accept=".js,.ts" disabled={!isFrameworkSupported} />
                </label>
                {!isFrameworkSupported && (
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2
                    px-2 py-1 text-xs text-white bg-[#383838] rounded-full"
                  >
                    Coming Soon
                  </span>
                )}
              </div> */}

              <button
                onClick={handleSave}
                disabled={!openApiSpec.trim() && selectedOption !== 'infer'}
                className="flex items-center justify-center gap-1.5 w-full px-2.5 py-2.5 bg-dualite-background-cta text-dualite-text-cta rounded-lg text-sm font-medium hover:bg-dualite-background-cta-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('modals.codeIntegration.continue')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CodeIntegration;
